import { useState } from 'react';
import AcronisIcon from '@liquidweb-dev/undercurrent/icons/platform/Acronis';
import Box from '@liquidweb-dev/undercurrent/Box';
import Button from '@liquidweb-dev/undercurrent/Button';
import CloudIcon from '@liquidweb-dev/undercurrent/icons/lucide/Cloud';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import RadioGroup from '@liquidweb-dev/undercurrent/RadioGroup';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import { AssetDetails } from 'modules/queries/asset/useDetails';
import useBrandName from 'utility/hooks/useBrandName';
import { useBackupTypeContext } from './context';
import BackupTile from '../BackupTile';

interface BackupTypeSelectProps {
	assetDetails?: AssetDetails;
	onClose?: () => void;
}

const BackupTypeSelect: React.FC<BackupTypeSelectProps> = ({
	assetDetails,
	onClose,
}) => {
	const [selectedBackupType, setSelectedBackupType] = useState<string>('None');

	const backup = useBackupTypeContext();
	const brandName = useBrandName();

	const canAcronis = assetDetails?.backupsState?.canAcronis;
	const canCloudBackup = assetDetails?.backupsState?.canCloudBackup;

	return (
		<>
			<DrawerHeader
				onClose={onClose}
				description={
					<Typography
						variant="body-sm"
						sx={({ vars }) => ({ color: vars.palette.uc.text.medium })}
					>
						Choose a backup solution to protect your data
					</Typography>
				}
			>
				Backup settings
			</DrawerHeader>
			<DrawerContent>
				<Stack direction="column" gap={300}>
					<Typography variant="label-lg" fontWeight={500}>
						Select backup type
						{assetDetails?.domain ? (
							<>
								{' '}
								for{' '}
								<Box
									component="span"
									sx={{
										color: ({ vars }) => vars.palette.uc.highlight.text.main,
									}}
								>
									{assetDetails.domain}
								</Box>
							</>
						) : null}
					</Typography>
					{canAcronis || canCloudBackup ? (
						<RadioGroup
							sx={{ gap: 150 }}
							value={selectedBackupType}
							onChange={(_, newValue) => setSelectedBackupType(newValue)}
						>
							{canAcronis && (
								<BackupTile
									value="AcronisBackup"
									label="Acronis Cyber Backups"
									description="Acronis Cloud delivers ultra-fast, secure, and flexible backup and recovery with military-grade encryption, unmatched speed, and complete control over all your data—anytime, anywhere."
									icon={
										<AcronisIcon
											sx={(theme) => ({
												height: theme.size.sm,
												width: theme.size.sm,
											})}
										/>
									}
									checked={selectedBackupType === 'AcronisBackup'}
								/>
							)}
							{canCloudBackup && (
								<BackupTile
									value="CloudBackup"
									label="Cloud Backups"
									description={`${brandName} provides automatic backups essential for critical systems. Use a saved image to quickly restore in case of issues.`}
									icon={
										<CloudIcon
											sx={(theme) => ({
												height: theme.size.sm,
												width: theme.size.sm,
											})}
										/>
									}
									checked={selectedBackupType === 'CloudBackup'}
								/>
							)}
						</RadioGroup>
					) : (
						<BackupTile
							label="No Daily Backups"
							description="If you choose this option, you will want to consider configuring backups available in your operating system or control panel."
						/>
					)}
				</Stack>
			</DrawerContent>
			<DrawerFooter spacing={100}>
				<Button
					disabled={selectedBackupType === 'None'}
					size="large"
					onClick={() => backup.setType(selectedBackupType)}
				>
					Configure
				</Button>
				<Button variant="ghost" onClick={onClose} size="large">
					Cancel
				</Button>
			</DrawerFooter>
		</>
	);
};

export default BackupTypeSelect;
